/**
 * The Core reducer is intended to contain any data core to the running of the app.
 *
 * If patterns in the core data are recognised that would be better served by another slice, then
 * extract a separate slice.
 */

import * as RTK from '@reduxjs/toolkit';
import { Api, Fixtuur } from '@fixtuur/backpack';
import { LogLevel } from '@dora/lumberjack';
import { ErrorContext } from './middleware/sentry';

/**
 * Error state for the app describes the type of error and any extra messaging from the error
 */
export type StellaError = {
    message: string;
    context: ErrorContext;
};

export interface State {
    /**
     * Is the app currently open and on display, or closed and invisible?
     */
    open: boolean;
    /** Unique client id string used to fetch product and asset data etc */
    clientId: string | undefined;

    /** Unique tenant name */
    tenant: Fixtuur.Tenant.TenantId | undefined;

    /** ISO locale string used for i18n */
    locale: string | undefined;

    /** ISO currency string used for price displays */
    currency: string | undefined;

    /** URL of the parent site outside of the iFrame, used when generating sharing links */
    currentUrl?: string;
    /**
     * URLs for each of the services the app requires. Likely to be set via the AppConfig object.
     */
    serviceUrls?: Api.ServiceConfig;
    /**
     * Feature flags passed from SDK
     */
    featureFlags: Record<string, boolean>;
    /**
     * Current error state of the app
     */
    error: StellaError | null;
    /**
     * The current log level of Stella
     */
    logLevel: LogLevel;
    /**
     * Brand colour to be used for theming the app
     */
    brandColour: string | undefined;
    /**
     * Logo used for some loading states
     */
    logoSrc: string | undefined;
    /**
     * Environment, e.g. dev, prod, to be passed to Sentry
     */
    environment: string | undefined;
    /**
     * Client key used to initialise Sentry
     */
    sentryDsn: string | undefined;
    measurementUnit: Fixtuur.Measurement.Unit;
}

export const initialState: State = {
    open: false,
    clientId: undefined,
    tenant: undefined,
    locale: undefined,
    currency: undefined,
    featureFlags: {},
    currentUrl: undefined,
    error: null,
    logLevel: LogLevel.Info,
    brandColour: undefined,
    logoSrc: undefined,
    environment: undefined,
    sentryDsn: undefined,
    measurementUnit: 'millimetre',
};

export const serviceUrl = (base: string, tenant: string) => `${base}/api/v1/${tenant}`;
export const toastUrl = (base: string, tenant: string) => `${base}/v1/${tenant}`;
export const arUrl = (base: string, tenant: string) => `${base}/${tenant}`;

export const slice = RTK.createSlice({
    name: 'core',
    initialState,
    reducers: {
        /** Trigger the initialisation of the application */
        initialise(state, action: RTK.PayloadAction<State>) {
            const {
                serviceUrls,
                clientId,
                featureFlags,
                locale,
                currency,
                logLevel,
                brandColour,
                logoSrc,
                environment,
                sentryDsn,
                tenant,
                measurementUnit,
            } = action.payload;

            state.clientId = clientId;
            state.locale = locale;
            state.currency = currency;
            state.brandColour = brandColour;
            state.logoSrc = logoSrc;
            state.logLevel = logLevel;
            state.environment = environment;
            state.tenant = tenant;
            state.sentryDsn = sentryDsn;
            state.serviceUrls = serviceUrls;

            // TODO: error handling
            state.featureFlags = featureFlags;
            state.measurementUnit = measurementUnit;
        },
        setOpen(state, _: RTK.Action) {
            state.open = true;
        },
        setClosed(state, _: RTK.Action) {
            state.open = false;
        },
        setCurrentUrl(state, action: RTK.PayloadAction<string>) {
            state.currentUrl = action.payload;
        },
        error(state, action: RTK.PayloadAction<StellaError>) {
            // Required to manage the state of StellaError, e.g: 'render' for Render Error view.
            if (action.payload.context.type !== 'log') {
                state.error = action.payload;
            }
        },
        clearError(state, _: RTK.PayloadAction) {
            state.error = null;
        },
    },
});

export const { initialise, setClosed, setOpen, setCurrentUrl, error, clearError } = slice.actions;

export const reducer = slice.reducer;
